import { Component, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { environment } from '@lohi/environments/environment';
import { VERSION } from '@lohi/environments/version';
import * as LogRocket from 'logrocket';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, pluck, take } from 'rxjs/operators';
import { AuthService } from './root/auth.service';
import { CompanyService } from './root/company.service';
import { DriversService } from './root/drivers.service';
import { FeatureFlagService } from './root/feature-flags.service';
import { IntercomService } from './root/intercom.service';
import { RouterStateService } from './root/router-state.service';
import { BannerService } from './shared/banner.service';
import { SnackBarService } from './shared/snackbar.service';
import { WebBanner } from './types';

// Fevid, Fevid, Starr, Swaggin, Caron
const visionExcluded = ['LCORG8U8IMN', 'LCO61DGM2RQ', 'LCOVB4LL4D7', 'LCOL403BSIA', 'LCOR7U4H43C'];

@Component({
  selector: 'lohi-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  public title = 'lohi-web';
  public env = environment;
  @ViewChild('sidenav') public drawer: MatDrawer;
  @ViewChild('settingsModal') private settingsModal: TemplateRef<any>;
  public showTitleBar$: Observable<boolean>;
  public showCommitmentBar$: Observable<boolean>;
  public regionsNotMeetingCommitment$: Observable<string[]>;
  public settingRef: MatDialogRef<TemplateRef<any>>;
  public companySettingsForm: FormGroup;
  public bannerMessage$: Observable<WebBanner>;
  public logo$: Observable<string>;
  private showSideNav = false;
  // Leave this here for the routing
  public isInternal$: Observable<boolean>;
  public carrierName$: Observable<string>;
  public earningsProgram$: Observable<boolean>;
  public hide5FBanner: boolean;
  public show2022VisionBanner$: Observable<boolean>;
  public enableTours$: Observable<boolean>;

  public isV2Dashboard = false;
  public isFinalCount = false;

  constructor(
    private router: Router,
    private routerState: RouterStateService,
    private authService: AuthService,
    // @ts-ignore
    private featureFlagService: FeatureFlagService,
    // @ts-ignore
    private intercomService: IntercomService,
    private matDialog: MatDialog,
    private fb: FormBuilder,
    private snackBar: SnackBarService,
    private driversService: DriversService,
    private companyService: CompanyService,
    private bannerService: BannerService,
  ) {
    this.showTitleBar$ = combineLatest([
      this.routerState.listenForQueryChange$('takeover').pipe(map((takeover) => takeover !== 'true')),
      this.routerState.routerState$.pipe(map((state) => !state.url.includes('/auth'))),
      this.routerState.routerState$.pipe(map((state) => !state.url.includes('/fivef-ratecon'))),
      this.routerState.routerState$.pipe(map((state) => !state.url.includes('/driver_load'))),
    ]).pipe(
      map(([notTakeoverBool, notLoginBool, notRateCon, notStatusPage]) => {
        this.showSideNav = notTakeoverBool && notLoginBool && notRateCon && notStatusPage;
        return notTakeoverBool && notLoginBool && notRateCon && notStatusPage;
      }),
    );

    this.showCommitmentBar$ = combineLatest([
      this.routerState.listenForQueryChange$('takeover').pipe(map((takeover) => takeover !== 'true')),
      this.routerState.routerState$.pipe(map((state) => !state.url.includes('/internal/'))),
    ]).pipe(
      map(([notTakeoverBool, notInternal]) => {
        return notTakeoverBool && notInternal;
      }),
    );

    this.carrierName$ = this.authService.userInfo$.pipe(pluck('companyName'));
    this.enableTours$ = this.authService.userInfo$.pipe(pluck('enableTours'));

    this.router.events.subscribe(() => {
      this.isV2Dashboard = this.router.url.startsWith('/v2/');
      this.isFinalCount = this.router.url.startsWith('/final_count/');
    });
  }

  public ngOnInit(): void {
    this.regionsNotMeetingCommitment$ = this.companyService.regionNamesNotMeetingCommitment$;
    this.bannerMessage$ = this.bannerService.webBanner$;
    this.logo$ = this.routerState.routerState$.pipe(
      map((rs) => {
        if (rs?.url?.startsWith('/pay')) {
          return '/assets/images/lohipay_logo.svg';
        }
        return '/assets/images/lohi_logo.svg';
      }),
    );
    this.earningsProgram$ = this.authService.userInfo$.pipe(
      map(
        (value) =>
          !value.isFiveFTourDispatcher &&
          value.earningsProgramMember &&
          !value.companyName.toLowerCase().startsWith('fevid') &&
          !value.companyName.toLowerCase().startsWith('starr') &&
          !value.companyName.toLowerCase().startsWith('caron') &&
          !value.companyName.toLowerCase().startsWith('swaggin wagon'),
      ),
    );
    this.hide5FBanner = false;
    this.show2022VisionBanner$ = this.authService.userInfo$.pipe(
      map((userInfo) => {
        if (
          !userInfo ||
          !userInfo.accountRelationship ||
          !userInfo.companyId ||
          !userInfo.earningsProgramMember ||
          userInfo.isFiveFTourDispatcher
        ) {
          return false;
        }
        if (userInfo.accountRelationship === 'saas') {
          return false;
        }
        if (visionExcluded.includes(userInfo.companyId)) {
          return false;
        }
        return true;
      }),
    );
    this.enableLogRocket();
  }

  private enableLogRocket() {
    if (window.location.hostname === 'localhost') {
      return;
    }
    const logRocketOptions = {
      release: this.getVersion(),
    };
    if (environment.production) {
      LogRocket.init('stkslu/lohi-prod', logRocketOptions);
    } else {
      LogRocket.init('stkslu/lohi-stage', logRocketOptions);
    }
    // } else {
    //   LogRocket.init('stkslu/vorto-reload-stage', logRocketOptions);
    // }

    this.authService.isLoggedIn$
      .pipe(
        filter((t) => t),
        take(1),
      )
      .subscribe(async (isLoggedIn) => {
        if (isLoggedIn) {
          const user = await this.authService.userInfo$
            .pipe(
              filter((u) => !!u && !!u.id),
              take(1),
            )
            .toPromise();
          LogRocket.identify(user.id, {
            name: user.driverName,
            phone: user.phoneNumber,
            // Add your own custom user variables here, ie:
            account: user.companyName,
            userPermissions: user.permissions.join(', '),
            createAt: user.verifiedTimestamp,
          });
        }
      });
  }

  private getVersion(): string {
    if (VERSION && VERSION.version) {
      if (VERSION.hash) {
        return `${VERSION.version}.${VERSION.hash}`;
      }
      return VERSION.version;
    }
  }

  @HostListener('document:keydown', ['$event'])
  public slashKey(event: KeyboardEvent) {
    if (!this.showSideNav) {
      return;
    }
    if ((event.target as HTMLElement).tagName !== 'BODY') {
      return;
    }
    if (event.key === '/') {
      return this.drawer.toggle();
    }

    if (event.key === 'Escape') {
      if (this.drawer.opened) {
        return this.drawer.close();
      }
    }
  }

  public async promptSettingsChange() {
    if (this.settingRef) {
      this.settingRef.close();
      this.settingRef = null;
    }
    if (this.companySettingsForm) {
      this.companySettingsForm = null;
    }

    const defaultCut = await this.driversService.getDefaultDriverPercentage();
    const userInfo = await this.authService.userInfo$.pipe(take(1)).toPromise();

    this.companySettingsForm = this.fb.group({
      defaultDriverCut: [defaultCut, [Validators.required]],
      allowDriverBooking: [userInfo.allowDriverBooking, [Validators.required]],
      allowDriverTruckCreate: [userInfo.allowDriverCreateTruck, [Validators.required]],
      allowDriverTrailerCreate: [userInfo.allowDriverCreateTrailer, [Validators.required]],
      allowDriversToViewTourDollarAmounts: [userInfo.companyDriversCanViewTourDollarAmounts, [Validators.required]],
      allowDriversToApproveTours: [userInfo.companyDriversCanApproveTours, [Validators.required]],
      allowDriversToReviewRateCon: [userInfo.companyDriversCanViewTourRateCon, [Validators.required]],
    });

    this.settingRef = this.matDialog.open(this.settingsModal, {
      panelClass: ['w-full', 'lg:w-1/2'],
    });
  }

  public async updateDefaultPercentage() {
    const percentage: number = this.companySettingsForm.get('defaultDriverCut').value;
    if (percentage === null) {
      this.snackBar.showError('Percentage must be between 0 and 100');
      return;
    }
    const success = await this.driversService.setDefaultDriverPercentage(percentage);
    if (success) {
      this.snackBar.showMessage('Default Percentage Updated');
    } else {
      this.snackBar.showError('Failed to update Default Percentage');
    }
  }

  public async updateAllowDriverShiftBooking() {
    const allowDriverBooking: boolean = this.companySettingsForm.get('allowDriverBooking').value;
    const success = await this.driversService.setAllowDriverBooking(allowDriverBooking);
    if (success) {
      this.snackBar.showMessage('Updated Driver Booking Settings');
    } else {
      this.snackBar.showError('Failed to update settings');
    }
  }

  public async updateAllowDriverTruckCreation() {
    const allowDriverTruckCreate: boolean = this.companySettingsForm.get('allowDriverTruckCreate').value;
    const success = await this.driversService.setAllowDriverTruck(allowDriverTruckCreate);
    if (success) {
      this.snackBar.showMessage('Updated Driver Truck Settings');
    } else {
      this.snackBar.showError('Failed to update settings');
    }
  }

  public async updateAllowDriverTrailerCreation() {
    const allowDriverTrailerCreate: boolean = this.companySettingsForm.get('allowDriverTrailerCreate').value;
    const success = await this.driversService.setAllowDriverTrailer(allowDriverTrailerCreate);
    if (success) {
      this.snackBar.showMessage('Updated Driver Trailer Settings');
    } else {
      this.snackBar.showError('Failed to update settings');
    }
  }

  public async updateAllowDriversToViewTourDollarAmounts() {
    const allowDriversToViewTourDollarAmounts: boolean = this.companySettingsForm.get(
      'allowDriversToViewTourDollarAmounts',
    ).value;
    const success = await this.driversService.setAllowDriversToViewTourDollarAmounts(
      allowDriversToViewTourDollarAmounts,
    );
    if (success) {
      this.snackBar.showMessage('Updated Drivers Can View Tour Dollar Values Setting');
    } else {
      this.snackBar.showError('Failed to update settings');
    }
  }

  public async updateAllowDriversToViewTourRateCon() {
    const allowDriversToViewTourRateCon: boolean = this.companySettingsForm.get('allowDriversToReviewRateCon').value;
    const success = await this.driversService.setAllowDriversToViewTourRateCon(allowDriversToViewTourRateCon);
    if (success) {
      const message = allowDriversToViewTourRateCon
        ? 'All drivers can view the rate con'
        : 'Drivers will not be able to see the rate con';
      this.snackBar.showMessage(message);
    } else {
      this.snackBar.showError('Failed to update settings');
    }
  }

  public async updateAllowDriversToApproveTours() {
    const allowDriversToApproveTours: boolean = this.companySettingsForm.get('allowDriversToApproveTours').value;
    const success = await this.driversService.setAllowDriversToApproveTours(allowDriversToApproveTours);
    if (success) {
      this.snackBar.showMessage('Updated Drivers Can Approve or Decline Tours Setting');
    } else {
      this.snackBar.showError('Failed to update settings');
    }
  }

  public openIntercom() {
    this.intercomService.show();
  }
}
